import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Linking } from 'react-native';
import noop from 'lodash/noop';
import FingerprintScanner from 'utils/packages/fingerprintScanner';
import { setTouchId } from 'actions';
import * as Icons from 'design-system/icons';
import { SettingsItem, SettingsItemSwitch } from 'design-system/SettingsItem';
import useAppState from 'hooks/useAppState';
import useColors from 'hooks/useColors';
import { selectBiometricsEnabled } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
const strings = {
    changePasscode: {
        title: 'Change Passcode',
    },
    setPasscode: {
        title: 'Set Passcode',
    },
    biometrics: {
        'Touch ID': 'Enable Touch ID',
        'Face ID': 'Enable Face ID',
        Biometrics: 'Enable biometrics',
    },
    disabledAlert: {
        title: (biometrics) => `${biometrics} is turned off`,
        notAllowed: (biometrics) => `To use ${biometrics}, allow Emma to use it in Settings.`,
        notSetUp: (biometrics) => `To use ${biometrics}, set it up in your device settings first.`,
        cancel: 'Cancel',
        openSettings: 'Open Settings',
    },
};
const isBiometrics = (value) => typeof value === 'string' && value in strings.biometrics;
const getSensorStatus = async () => {
    try {
        const biometrics = await FingerprintScanner.isSensorAvailable();
        return { biometrics };
    }
    catch (e) {
        const error = e;
        switch (error.name) {
            case 'DeviceLockedPermanent':
                return isBiometrics(error.biometric)
                    ? { biometrics: error.biometric }
                    : { biometrics: null };
            case 'FingerprintScannerNotAvailable':
            case 'FingerprintScannerNotEnrolled':
            case 'PasscodeNotSet':
                return isBiometrics(error.biometric)
                    ? { biometrics: error.biometric, error: error.name }
                    : { biometrics: null };
            default:
                return { biometrics: null };
        }
    }
};
const showBiometricsDisabledAlert = (biometrics, error) => {
    Alert.alert(strings.disabledAlert.title(biometrics), error === 'FingerprintScannerNotAvailable'
        ? strings.disabledAlert.notAllowed(biometrics)
        : strings.disabledAlert.notSetUp(biometrics), [
        { text: strings.disabledAlert.cancel, style: 'cancel' },
        {
            text: strings.disabledAlert.openSettings,
            onPress: () => Linking.openSettings().catch(noop),
        },
    ]);
};
export const SecuritySettingsSection = () => {
    const colors = useColors();
    const { navigate } = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const isTouchActive = useAppSelector(selectBiometricsEnabled);
    const appState = useAppState();
    const [sensorStatus, setSensorStatus] = useState({
        biometrics: null,
    });
    useEffect(() => {
        if (appState === 'active') {
            getSensorStatus().then(setSensorStatus);
        }
    }, [appState]);
    const hasPinSet = useAppSelector((store) => store.user.user.pinLastChangedAt);
    const onPressChangePasscode = useCallback(() => {
        if (hasPinSet) {
            navigate('ChangePasscode');
        }
        else {
            navigate('SetupPasscode', { allowBack: true, popOnComplete: true });
        }
    }, [hasPinSet]);
    const onBiometricsValueChange = useCallback((val) => {
        dispatch(setTouchId(val));
    }, []);
    const onPressDisabledBiometrics = useCallback(() => {
        if (sensorStatus.biometrics && sensorStatus.error) {
            showBiometricsDisabledAlert(sensorStatus.biometrics, sensorStatus.error);
        }
    }, [sensorStatus]);
    if (isWeb && !hasPinSet) {
        return null;
    }
    return (<>
      <SettingsItem title={hasPinSet ? strings.changePasscode.title : strings.setPasscode.title} icon={<Icons.IconLock color={colors.gradients.deepPink.colors[0]}/>} onPress={onPressChangePasscode}/>
      {sensorStatus.biometrics ? (<SettingsItemSwitch title={strings.biometrics[sensorStatus.biometrics]} icon={sensorStatus.biometrics === 'Face ID' ? (<Icons.IconFaceID />) : (<Icons.IconFingerprint />)} onValueChange={onBiometricsValueChange} value={!sensorStatus.error && (isTouchActive ?? false)} disabled={!!sensorStatus.error} onPressOnDisabled={onPressDisabledBiometrics}/>) : null}
    </>);
};
